/**
 * CTA Section Component
 *
 * EXTRACTED FROM: modash.io (verified reference implementation)
 *
 * Call-to-action section with dark or accent background.
 * Supports primary/secondary buttons and descriptive text.
 *
 * Key styling (matches reference):
 * - Pink background: #ffa3d1
 * - Border radius: 20px (card)
 * - Padding: 160px 40px 64px (large top padding)
 * - Headline: clamp(40px, 10vw, 136px), line-height 0.9
 * - Primary button: BLACK (#141414) on pink, white on dark
 * - Secondary button: Light transparent background
 * - Trial text: 14px (not 12px)
 */
"use client"
import Link from "next/link"
import { T } from "gt-next"

interface CTASectionProps {
    title: React.ReactNode
    primaryCta?: {
        text: React.ReactNode
        href: string
    }
    secondaryCta?: {
        text: React.ReactNode
        href: string
    }
    note?: React.ReactNode
    variant?: "dark" | "pink" | "purple"
    useAuthButton?: boolean
    className?: string
}

export default function CTASection({ title, primaryCta = { text: <T>Start growing</T>, href: "/setup" }, secondaryCta, note = <T>Cancel anytime</T>, variant = "pink", useAuthButton = true, className = "" }: CTASectionProps) {
    // Background colors
    const bgColors = {
        dark: "#141414",
        pink: "#ffa3d1",
        purple: "#c08cf2"
    }

    // Text colors
    const titleColor = variant === "dark" ? "white" : "#141414"
    const noteColor = variant === "dark" ? "rgba(255, 255, 255, 0.6)" : "rgba(20, 20, 20, 0.6)"

    // Button styles (matches reference exactly)
    const primaryButtonStyle = {
        backgroundColor: variant === "dark" ? "white" : "#141414",
        color: variant === "dark" ? "#141414" : "white"
    }

    const secondaryButtonStyle = {
        backgroundColor: variant === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(59, 59, 43, 0.08)",
        color: titleColor
    }

    return (
        <section className={`bg-white px-3 py-3 ${className}`}>
            <div className="flex w-full flex-col items-center justify-center gap-8 rounded-card px-4 py-12 md:px-6 md:py-16 lg:px-10 lg:py-20" style={{ backgroundColor: bgColors[variant] }}>
                {/* Title */}
                <h2
                    className="max-w-[800px] text-center font-mosans font-normal uppercase leading-[0.9]"
                    style={{
                        fontSize: "clamp(40px, 10vw, 136px)",
                        color: titleColor
                    }}>
                    {title}
                </h2>

                {/* CTA Buttons Section */}
                <div className="flex flex-col items-center gap-4">
                    <div className="flex flex-row flex-wrap items-center justify-center gap-4">
                        {/* Primary Button - BLACK on pink background */}
                        {/* CONVERSION-OPTIMIZED: 56px mobile → 48px desktop */}
                        {primaryCta && (
                            <Link href={primaryCta.href} className={`${variant !== "dark" ? "btn-inset-dark " : ""}inline-flex h-14 items-center justify-center rounded-xl px-6 py-4 text-[16px] font-semibold leading-[150%] no-underline transition-[opacity,transform,scale] hover:opacity-90 active:scale-[0.98] md:h-12 md:px-5 md:py-3`} style={primaryButtonStyle}>
                                {primaryCta.text}
                            </Link>
                        )}

                        {/* Secondary Button - Light transparent background */}
                        {/* CONVERSION-OPTIMIZED: 56px mobile → 48px desktop */}
                        {secondaryCta && (
                            <Link
                                href={secondaryCta.href}
                                className={`${variant === "dark" ? "btn-inset-dark " : ""}inline-flex h-14 items-center justify-center rounded-xl border-none px-6 py-4 text-[16px] font-semibold leading-[150%] no-underline transition-[opacity,transform,scale] hover:opacity-90 active:scale-[0.98] md:h-12 md:px-5 md:py-3`}
                                style={secondaryButtonStyle}>
                                {secondaryCta.text}
                            </Link>
                        )}
                    </div>

                    {/* Trial Text - VERIFIED: 14px (not 12px) */}
                    {note && (
                        <p className="m-0 text-center text-[14px] leading-[140%]" style={{ color: noteColor }}>
                            {note}
                        </p>
                    )}
                </div>
            </div>
        </section>
    )
}
